import React, { useCallback, useEffect, useState } from 'react';
import { aaiApi } from '../../services/aaiApi';
import { useParticipant } from '../../hooks/useParticipant';
import type { AaiHome } from '../../types/aai';
import { Loading, ErrorNote } from './components';
import DeptHeadHome from './dashboards/DeptHeadHome';
import DepartmentTab from './DepartmentTab';

// "Department Overview" — a department head's landing (design §6.1): the team's
// readiness band, participation, time-redirected range, Educate→Empower→Equip
// progress, the roster (stage only, individual numbers locked), and the gate-1
// approval queue. Served by GET /home with the department-head lens. Other roles
// who deep-link here fall back to the aggregate rollup.
const DepartmentPage: React.FC = () => {
  const { context, ready } = useParticipant();
  const [home, setHome] = useState<AaiHome | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await aaiApi.getHome();
      setHome(data);
    } catch {
      setError('Could not load your department.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !ready) return <div className="max-w-5xl mx-auto"><Loading label="Loading your department…" /></div>;
  if (error) return <div className="max-w-5xl mx-auto"><ErrorNote message={error} /></div>;

  return (
    <div className="max-w-5xl mx-auto">
      {home && home.role === 'department_head' ? (
        <DeptHeadHome data={home} onRefresh={load} />
      ) : (
        <DepartmentTab department={context?.department ?? null} />
      )}
    </div>
  );
};

export default DepartmentPage;
